import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'; // Añadido useRef
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import StockCounter from '../../components/ui/StockCounter';
import PaymentStatusNotification from '../../components/ui/PaymentStatusNotification';
import QuantitySelector from './components/QuantitySelector';
import UserInformationForm from './components/UserInformationForm';
import PaymentMethodSelector from './components/PaymentMethodSelector';
import PaymentDetailsForm from './components/PaymentDetailsForm';
import FileUploadComponent from './components/FileUploadComponent';
import PurchaseSummary from './components/PurchaseSummary';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
// ✅ Ruta CORRECTA para src/pages/purchase-flow/index.jsx
import { supabase } from '../../supabaseClient'; 

const PurchaseFlow = () => {
  const navigate = useNavigate();
  const quantityRef = useRef(); // ← NUEVO: Ref para QuantitySelector
  const summaryRef = useRef(); // ← NUEVO: Ref para PurchaseSummary

  const [currentStep, setCurrentStep] = useState(1);

  // Nuevo: controla si se puede procesar pago (evita popup/ejecución al iniciar)
  const [canProceed, setCanProceed] = useState(false);

  // Usamos solo isProcessing (eliminamos isSubmitting por completo)
  const [isProcessing, setIsProcessing] = useState(false);
  const [purchaseStatus, setPurchaseStatus] = useState(null); // 'success' | 'error' | null

  // Estados del formulario
  const [quantity, setQuantity] = useState(1);
  const [formData, setFormData] = useState({
    cedula: '',
    name: '',
    email: '',
    phone: ''
  });
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [paymentData, setPaymentData] = useState({
    bankIssuer: '',
    reference: '',
    amount: '',
    transferDate: new Date()?.toISOString()?.split('T')?.[0],
    zelleConfirmation: '',
    transactionHash: '',
    network: '',
    cryptoAmount: ''
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [errors, setErrors] = useState({});
  // Estado para almacenar los números de tickets seleccionados en modo manual
  const [selectedTicketNumbers, setSelectedTicketNumbers] = useState(new Set());
  // Estado para almacenar el ID del sorteo activo
  const [activeRaffleId, setActiveRaffleId] = useState(null); // Añadido

  const ticketPrice = 50;
  const maxQuantity = 10;

  const totalAmount = useMemo(() => quantity * ticketPrice, [quantity, ticketPrice]);

  // Efecto para obtener el ID del sorteo activo al montar el componente
  useEffect(() => {
    const fetchActiveRaffle = async () => {
      try {
        const { data: raffleData, error: raffleError } = await supabase
          .from('raffles')
          .select('id')
          .eq('estado', 'activo')
          .single();

        if (raffleError) throw raffleError;
        if (!raffleData) throw new Error("No hay un sorteo activo.");

        setActiveRaffleId(raffleData.id);
        console.log("Sorteo activo encontrado en PurchaseFlow:", raffleData.id);

      } catch (error) {
        console.error("Error al obtener el sorteo activo en PurchaseFlow:", error);
        setPurchaseStatus('error');
      }
    };

    fetchActiveRaffle();
  }, []); // Se ejecuta una sola vez al montar

  useEffect(() => {
    setPaymentData(prev => ({ ...prev, amount: totalAmount?.toString() }));
  }, [totalAmount]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  // Nuevo: habilita el botón de procesar solo si hay al menos 2 boletos seleccionados o quantity >= 2
  useEffect(() => {
    const hasMultipleSelectedTickets = !!selectedTicketNumbers && selectedTicketNumbers.size >= 2;
    if (hasMultipleSelectedTickets || quantity >= 2) {
      setCanProceed(true);
    } else {
      setCanProceed(false);
    }
  }, [selectedTicketNumbers, quantity]);

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData?.cedula || formData?.cedula?.length < 7) {
      newErrors.cedula = 'Cédula requerida (mínimo 7 dígitos)';
    }
    if (!formData?.name || formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Nombre completo requerido';
    }
    if (!formData?.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Email válido requerido';
    }
    if (!formData?.phone || formData?.phone?.replace(/\D/g, '')?.length !== 11) {
      newErrors.phone = 'Teléfono válido requerido';
    }
    if (!selectedPaymentMethod) {
      newErrors.paymentMethod = 'Selecciona un método de pago';
    }
    if (selectedPaymentMethod === 'pago_movil') {
      if (!paymentData?.bankIssuer) newErrors.bankIssuer = 'Selecciona tu banco';
      if (!paymentData?.reference || paymentData?.reference?.length !== 6) {
        newErrors.reference = 'Referencia de 6 dígitos requerida';
      }
    } else if (selectedPaymentMethod === 'zelle') {
      if (!paymentData?.zelleConfirmation) {
        newErrors.zelleConfirmation = 'Número de confirmación requerido';
      }
    } else if (selectedPaymentMethod === 'binance') {
      if (!paymentData?.transactionHash) {
        newErrors.transactionHash = 'Hash de transacción requerido';
      }
      if (!paymentData?.network) newErrors.network = 'Selecciona la red';
      if (!paymentData?.cryptoAmount) {
        newErrors.cryptoAmount = 'Monto en cripto requerido';
      }
    }
    if (!paymentData?.amount) {
      newErrors.amount = 'Monto requerido';
    }
    if (!uploadedFile) {
      newErrors.file = 'Comprobante de pago requerido';
    }
    return { errors: newErrors, isValid: Object.keys(newErrors)?.length === 0 };
  }, [formData, selectedPaymentMethod, paymentData, uploadedFile]);

  const formValidation = useMemo(() => {
    return validateForm();
  }, [validateForm]);

  // Función para manejar la navegación después de que PurchaseSummary haya procesado la compra
  const handleNavigationToConfirmation = useCallback(async () => { // Añadido async
    // --- NUEVO: Lógica para recargar tickets en QuantitySelector ---
    if (quantityRef.current?.refreshTickets) {
        try {
            console.log("🔄 Recargando lista de tickets...");
            await quantityRef.current.refreshTickets(); // Llama a la función expuesta
            console.log("✅ Lista de tickets recargada exitosamente.");
        } catch (err) {
            console.error("❌ Error al recargar tickets:", err);
        }
    } else {
        console.warn("⚠️ No se encontró el ref de QuantitySelector o no tiene el método refreshTickets.");
    }
    // --- Fin de la nueva lógica ---

    navigate('/payment-confirmation', {
      state: {
        quantity,
        totalAmount,
        userInfo: formData,
        paymentMethod: selectedPaymentMethod,
        status: 'pendiente_verificacion' // Estado inicial después de la inserción
      }
    });
  }, [navigate, quantity, totalAmount, formData, selectedPaymentMethod]); // Añadidas dependencias

  const handleFileUpload = useCallback((file, error) => {
    if (error) {
      setErrors(prev => ({ ...prev, file: error }));
      setUploadedFile(null);
    } else {
      setErrors(prev => ({ ...prev, file: undefined }));
      setUploadedFile(file);
    }
  }, []);

  // Función para que QuantitySelector notifique los tickets seleccionados
  const handleTicketsSelected = (selectedSet) => {
    setSelectedTicketNumbers(selectedSet);
  };

  // --- Nuevo: función que llama al proceso del resumen (el trabajo real está en PurchaseSummary)
  const handleProcessPurchase = useCallback(async () => {
    if (isProcessing) return; // evita doble clic
    setIsProcessing(true);
    setPurchaseStatus(null);

    try {
      if (summaryRef.current?.process) {
        await summaryRef.current.process();
        // PurchaseSummary informará resultado vía onProcessStatus prop
      } else {
        console.warn("No se encontró método process en PurchaseSummary ref. Intentando fallback.");
        setPurchaseStatus('error');
      }
    } catch (err) {
      console.error("Error al procesar la compra desde PurchaseFlow:", err);
      setPurchaseStatus('error');
    } finally {
      setIsProcessing(false);
    }
  }, [isProcessing]);

  const steps = [
    { id: 1, title: 'Cantidad', icon: 'Hash' },
    { id: 2, title: 'Información', icon: 'User' },
    { id: 3, title: 'Pago', icon: 'CreditCard' },
    { id: 4, title: 'Comprobante', icon: 'Upload' }
  ];

  const getStepStatus = (stepId) => {
    if (stepId < currentStep) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'pending';
  };

  // Construimos el objeto que espera PaymentStatusNotification
  const paymentStatusObj = useMemo(() => {
    if (purchaseStatus === 'success') {
      return { type: 'success', message: '¡Compra exitosa! Revisa tu email.' };
    }
    if (purchaseStatus === 'error') {
      return { type: 'failed', message: 'Pago fallido. Inténtalo de nuevo.' };
    }
    return null;
  }, [purchaseStatus]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <PaymentStatusNotification
        paymentStatus={paymentStatusObj}
        onDismiss={() => setPurchaseStatus(null)}
        onViewDetails={() => {}}
      />
      <div className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 lg:px-6">
          <div className="text-center mb-8">
            <h1 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Comprar Boletos
            </h1>
            <p className="text-lg text-text-secondary font-body max-w-2xl mx-auto">
              Completa tu compra de boletos para participar en el sorteo de Áurea Fortuna
            </p>
          </div>

          <div className="mb-8">
            <div className="flex items-center justify-center space-x-4 lg:space-x-8">
              {steps?.map((step, index) => (
                <React.Fragment key={step?.id}>
                  <div className="flex items-center space-x-2">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-premium ${
                      getStepStatus(step?.id) === 'completed'
                        ? 'bg-success text-white'
                        : getStepStatus(step?.id) === 'current' ?'bg-secondary text-secondary-foreground' :'bg-muted text-muted-foreground'
                    }`}>
                      {getStepStatus(step?.id) === 'completed' ? (
                        <Icon name="Check" size={16} />
                      ) : (
                        <Icon name="step?.icon" size={16} />
                      )}
                    </div>
                    <span className={`hidden sm:block text-sm font-caption ${
                      getStepStatus(step?.id) === 'current' ?'text-foreground font-medium' :'text-muted-foreground'
                    }`}>
                      {step?.title}
                    </span>
                  </div>
                  {index < steps?.length - 1 && (
                    <div className={`w-8 lg:w-16 h-0.5 transition-premium ${
                      getStepStatus(step?.id) === 'completed'
                        ? 'bg-success' :'bg-muted'
                    }`} />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <QuantitySelector
                ref={quantityRef}
                quantity={quantity}
                onQuantityChange={setQuantity}
                ticketPrice={ticketPrice}
                maxQuantity={maxQuantity}
                onTicketsSelected={handleTicketsSelected}
                disabled={isProcessing}
              />
              <UserInformationForm
                formData={formData}
                onFormChange={setFormData}
                errors={errors}
                disabled={isProcessing}
              />
              <PaymentMethodSelector
                selectedMethod={selectedPaymentMethod}
                onMethodSelect={setSelectedPaymentMethod}
                disabled={isProcessing}
              />
              <PaymentDetailsForm
                paymentData={paymentData}
                onPaymentDataChange={setPaymentData}
                selectedMethod={selectedPaymentMethod}
                errors={errors}
                disabled={isProcessing}
              />
              <FileUploadComponent
                onFileUpload={handleFileUpload}
                uploadedFile={uploadedFile}
                error={errors?.file}
                disabled={isProcessing}
              />
              {/* Mobile Submit Button */}
              <div className="lg:hidden">
                <Button
                  variant="secondary"
                  size="lg"
                  fullWidth
                  onClick={handleProcessPurchase} // Ahora llama a handler central
                  disabled={!canProceed || !formValidation?.isValid || isProcessing}
                  loading={isProcessing}
                  iconName="CreditCard"
                  iconPosition="left"
                >
                  {isProcessing ? 'Procesando...' : 'Procesar Compra'}
                </Button>
              </div>
            </div>

            <div className="lg:col-span-1">
              <PurchaseSummary
                ref={summaryRef} // ← Exponemos process() vía ref
                quantity={quantity}
                ticketPrice={ticketPrice}
                formData={formData}
                paymentData={paymentData}
                selectedMethod={selectedPaymentMethod}
                uploadedFile={uploadedFile}
                onSubmit={handleNavigationToConfirmation}
                onProcessStatus={(status) => {
                  // PurchaseSummary informará 'success' o 'error'
                  setPurchaseStatus(status);
                }}
                errors={errors}
                selectedTickets={selectedTicketNumbers}
                raffleId={activeRaffleId}
                canProceed={canProceed}
                isProcessing={isProcessing} // sólo isProcessing ahora
              />
            </div>
          </div>

          <div className="mt-12 text-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/home-landing')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Volver al Inicio
            </Button>
          </div>
        </div>
      </div>


    </div>
  );
};

export default PurchaseFlow;